export interface Period { from: string; to: string }

export function isoDay(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function monthRange(offset: number): Period {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
  return { from: isoDay(start), to: isoDay(end) };
}

export function yearToDate(): Period {
  return { from: isoDay(new Date(new Date().getFullYear(), 0, 1)), to: isoDay(new Date()) };
}

export function dayRange(offset: number): Period {
  const day = new Date();
  day.setDate(day.getDate() + offset);
  const value = isoDay(day);
  return { from: value, to: value };
}

export function relativeDays(days: number): Period {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days + 1);
  return { from: isoDay(from), to: isoDay(to) };
}

export function weekRange(offset: number): Period {
  const now = new Date();
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay() + 6) % 7) + offset * 7);
  const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
  return { from: isoDay(monday), to: isoDay(sunday) };
}

export function quarterRange(offset: number): Period {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3) + offset;
  const start = new Date(now.getFullYear(), quarter * 3, 1);
  const end = new Date(now.getFullYear(), quarter * 3 + 3, 0);
  return { from: isoDay(start), to: isoDay(end) };
}

export function yearRange(offset: number): Period {
  const year = new Date().getFullYear() + offset;
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

export const periodPresets: ReadonlyArray<{ label: string; value: () => Period }> = [
  { label: "Today", value: () => dayRange(0) },
  { label: "Yesterday", value: () => dayRange(-1) },
  { label: "Last 7 days", value: () => relativeDays(7) },
  { label: "This week", value: () => weekRange(0) },
  { label: "Previous week", value: () => weekRange(-1) },
  { label: "This month", value: () => monthRange(0) },
  { label: "Previous month", value: () => monthRange(-1) },
  { label: "This quarter", value: () => quarterRange(0) },
  { label: "Previous quarter", value: () => quarterRange(-1) },
  { label: "Year to date", value: yearToDate },
  { label: "This year", value: () => yearRange(0) },
  { label: "Previous year", value: () => yearRange(-1) },
  { label: "All history", value: () => ({ from: "2020-01-01", to: isoDay(new Date()) }) },
];

// A readable label for a range, used in the closed filter summary so the
// period stays visible without opening anything.
export function describe(period: Period): string {
  const month = monthRange(0);
  if (period.from === month.from && period.to === month.to) return "This month";
  const previous = monthRange(-1);
  if (period.from === previous.from && period.to === previous.to) return "Previous month";
  const year = yearToDate();
  if (period.from === year.from && period.to === year.to) return "Year to date";
  const format = (day: string) => new Intl.DateTimeFormat("en", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${day}T00:00:00Z`));
  return `${format(period.from)} — ${format(period.to)}`;
}
